import type { ColorType } from '@shared/types';

type HoldColor = ColorType | 'grey';

interface HoldProps {
  color?: HoldColor;
  shape?: 'rect' | 'hex';
  width?: number;
  height?: number;
  glow?: boolean;
  dim?: boolean;
  imageSrc?: string | null;
}

const PALETTE: Record<HoldColor, { base: string; light: string }> = {
  red:    { base: '#E63946', light: '#ff8a92' },
  blue:   { base: '#3B82F6', light: '#7ea9f7' },
  yellow: { base: '#F4C724', light: '#ffe066' },
  purple: { base: '#9B5DE5', light: '#c084fc' },
  grey:   { base: '#6b7280', light: '#9ca3af' },
};

export function Hold({
  color = 'red',
  shape = 'rect',
  width,
  height,
  glow = false,
  dim = false,
  imageSrc = null,
}: HoldProps) {
  const w = width ?? (shape === 'rect' ? 110 : 44);
  const h = height ?? (shape === 'rect' ? 32 : 44);
  const c = PALETTE[color];
  const glowCss = glow && !dim
    ? `drop-shadow(0 0 ${Math.min(w, h) / 2}px ${c.base}) drop-shadow(0 0 4px ${c.base})`
    : 'drop-shadow(0 3px 0 rgba(0,0,0,0.3))';

  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        alt=""
        width={w}
        height={h}
        style={{ display: 'block', filter: glowCss, opacity: dim ? 0.32 : 1 }}
      />
    );
  }

  if (shape === 'hex') {
    const gradId = `h-${color}-${w}`;
    return (
      <div style={{ width: w, height: h, filter: glowCss, opacity: dim ? 0.32 : 1 }}>
        <svg viewBox="0 0 100 100" width={w} height={h}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c.light} />
              <stop offset="100%" stopColor={c.base} />
            </linearGradient>
          </defs>
          <polygon
            points="50,4 92,28 92,72 50,96 8,72 8,28"
            fill={`url(#${gradId})`}
            stroke="#ffffff"
            strokeWidth="6"
            strokeLinejoin="round"
          />
          <path
            d="M 22 25 L 50 12 L 78 25 L 70 32 L 50 22 L 30 32 Z"
            fill="rgba(255,255,255,0.45)"
          />
          <polygon
            points="50,18 78,32 78,68 50,82 22,68 22,32"
            fill="none"
            stroke="rgba(0,0,0,0.18)"
            strokeWidth="2"
          />
        </svg>
      </div>
    );
  }

  const gradId = `r-${color}-${w}`;
  return (
    <div style={{ width: w, height: h, filter: glowCss, opacity: dim ? 0.32 : 1 }}>
      <svg viewBox="0 0 220 64" width={w} height={h}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c.light} />
            <stop offset="45%" stopColor={c.base} />
            <stop offset="100%" stopColor={c.base} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        <ellipse cx="110" cy="60" rx="95" ry="5" fill="rgba(0,0,0,0.45)" />
        <path
          d="M 14 30 Q 16 10 48 6 Q 120 3 178 10 Q 206 14 208 32 Q 207 52 188 56 Q 120 60 60 57 Q 22 55 14 38 Z"
          fill={`url(#${gradId})`}
          stroke="rgba(0,0,0,0.55)"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path
          d="M 28 13 Q 100 7 188 14 Q 180 21 100 21 Q 32 21 28 13 Z"
          fill="rgba(255,255,255,0.4)"
        />
        <circle cx="55" cy="38" r="2" fill="rgba(255,255,255,0.42)" />
        <circle cx="78" cy="44" r="1.4" fill="rgba(255,255,255,0.32)" />
        <circle cx="138" cy="40" r="2" fill="rgba(255,255,255,0.4)" />
        <circle cx="166" cy="35" r="1.5" fill="rgba(255,255,255,0.35)" />
        <ellipse cx="60" cy="48" rx="6" ry="3" fill="rgba(0,0,0,0.32)" />
        <ellipse cx="110" cy="52" rx="5" ry="2.5" fill="rgba(0,0,0,0.28)" />
        <ellipse cx="158" cy="48" rx="6" ry="3" fill="rgba(0,0,0,0.32)" />
      </svg>
    </div>
  );
}
